import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type WheelEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

interface LogoSliderItem {
  content: ReactNode;
  label: string;
}

interface LogoSliderProps {
  ariaLabel: string;
  compact?: boolean;
  heading: ReactNode;
  logos: LogoSliderItem[];
}

interface DragState {
  pointerId: number;
  scrollLeft: number;
  startX: number;
}

export default function LogoSlider({ ariaLabel, compact = false, heading, logos }: LogoSliderProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const instructionsId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [trackWidth, setTrackWidth] = useState(0);

  const isPaused = isDragging || isFocused || isHovered || prefersReducedMotion;
  const trackSpacing = compact
    ? 'gap-16 pr-16'
    : 'gap-16 pr-16 sm:gap-[110px] sm:pr-[110px]';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);

    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateWidth = () => {
      const nextWidth = Math.round(track.getBoundingClientRect().width);
      setTrackWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(track);

    return () => observer.disconnect();
  }, [logos]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !trackWidth || isPaused) return;

    let animationFrame = 0;
    let previousTime = performance.now();
    let scrollLeft = viewport.scrollLeft;

    const advance = (time: number) => {
      const distance = (time - previousTime) * 0.024;
      previousTime = time;
      scrollLeft += distance;
      if (scrollLeft >= trackWidth) scrollLeft -= trackWidth;
      viewport.scrollLeft = scrollLeft;
      animationFrame = requestAnimationFrame(advance);
    };

    animationFrame = requestAnimationFrame(advance);
    return () => cancelAnimationFrame(animationFrame);
  }, [isPaused, trackWidth]);

  const normalizeScrollPosition = () => {
    const viewport = viewportRef.current;
    if (viewport && trackWidth && viewport.scrollLeft >= trackWidth) {
      viewport.scrollLeft -= trackWidth;
    }
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (event.deltaX || !event.deltaY) return;

    event.preventDefault();
    event.currentTarget.scrollLeft += event.deltaY;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return;

    dragRef.current = {
      pointerId: event.pointerId,
      scrollLeft: event.currentTarget.scrollLeft,
      startX: event.clientX,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.currentTarget.scrollLeft = drag.scrollLeft - (event.clientX - drag.startX);
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;

    dragRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.currentTarget.scrollBy({ left: -80 });
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.currentTarget.scrollBy({ left: 80 });
    }
  };

  return (
    <section className="bg-secondary overflow-hidden rounded-2xl py-8" aria-label={ariaLabel}>
      <header className="text-primary px-6 sm:px-12">{heading}</header>
      <ul className="sr-only" aria-label={`${ariaLabel} logos`}>
        {logos.map((logo) => (
          <li key={logo.label}>{logo.label}</li>
        ))}
      </ul>
      <p id={instructionsId} className="sr-only">
        Scroll horizontally or use the left and right arrow keys to explore all logos.
      </p>
      <div
        ref={viewportRef}
        className="no-scrollbar h-20 cursor-grab select-none overflow-x-auto overscroll-x-contain rounded-lg px-6 outline-none active:cursor-grabbing focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-gray-60 sm:px-12"
        role="region"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-describedby={instructionsId}
        onBlur={() => setIsFocused(false)}
        onDragStart={(event) => event.preventDefault()}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onPointerCancel={endDrag}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onScroll={normalizeScrollPosition}
        onWheel={handleWheel}
      >
        <div className="flex w-max">
          <ul
            ref={trackRef}
            aria-hidden="true"
            className={`flex h-20 list-none items-center ${trackSpacing}`}
          >
            {logos.map((logo) => (
              <li key={logo.label} className="flex h-20 shrink-0 items-center justify-center">
                {logo.content}
              </li>
            ))}
          </ul>
          <ul
            aria-hidden="true"
            className={`flex h-20 list-none items-center ${trackSpacing}`}
          >
            {logos.map((logo) => (
              <li key={logo.label} className="flex h-20 shrink-0 items-center justify-center">
                {logo.content}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export type { LogoSliderItem };
